import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ArrowLeft, FileText, Image as ImageIcon, Video, File } from 'lucide-react';
import ReactPlayer from 'react-player';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import api from '../../api'; // Import the api instance

// PDF.js worker configuration
// Fix 17/09/2026 : worker local (cdnjs inaccessible + version .mjs de pdfjs-dist v5)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Interfaces
interface Module {
  _id: string;
  title: string;
  type: 'video' | 'text' | 'image' | 'pdf' | 'document';
  duration: number;
  content: string;
  fileType?: string;
}

interface Section {
  _id: string;
  title: string;
  modules: Module[];
}

interface Course {
  _id: string;
  title: string;
  sections: Section[];
}

const ModulePlayer: React.FC = () => {
  const { id, sectionId, moduleId } = useParams<{ id: string; sectionId: string; moduleId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [module, setModule] = useState<Module | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [pageNumber, setPageNumber] = React.useState(1);

  useEffect(() => {
    const fetchCourseAndModule = async () => {
      try {
        // Fix 17/09/2026 : endpoint générique (l'étudiant n'a pas accès à /admin)
        const response = await api.get(`/courses/${id}`);
        // /api/courses/:id renvoie { success, data: course } — Fix 17/09/2026
        const fetchedCourse = response.data.data || response.data;
        setCourse(fetchedCourse);

        const section = fetchedCourse.sections.find((s: Section) => s._id === sectionId);
        if (section) {
          const foundModule = section.modules.find((m: Module) => m._id === moduleId);
          if (foundModule) {
            setModule(foundModule);
          } else {
            setError(`Module non trouvé (ID: ${moduleId})`);
          }
        } else {
          setError(`Section non trouvée (ID: ${sectionId})`);
        }
      } catch (err) {
        setError('Erreur lors de la récupération des données du cours.');
        console.error(err);
      }
    };

    if (id && sectionId && moduleId) {
      fetchCourseAndModule();
    }
  }, [id, sectionId, moduleId]);

  // Fix 17/09/2026 : le fichier peut être dans fileUrl (upload) ou content/externalUrl
  const moduleSource: string = (module as any)?.fileUrl || (module as any)?.content || (module as any)?.externalUrl || '';

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const renderModuleContent = () => {
    if (!module) return null;

    switch (module.type) {
      case 'video':
        return (
          <div className="flex justify-center items-center p-6">
            <div className="player-wrapper w-full" style={{ position: 'relative', paddingTop: '56.25%' }}>
              <ReactPlayer
                src={moduleSource}
                className="react-player"
                width="100%"
                height="100%"
                controls
                style={{ position: 'absolute', top: 0, left: 0 }}
                
              />
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="prose max-w-none p-6">
            <h2>{module.title}</h2>
            <p className="whitespace-pre-line">{moduleSource}</p>
          </div>
        );

      case 'image':
        return (
          <div className="flex justify-center items-center p-6">
            <img 
              src={moduleSource} 
              alt={module.title} 
              className="max-w-full h-auto max-h-[70vh] rounded-lg shadow-md"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-image.png';
              }}
            />
          </div>
        );

      case 'pdf':
        // Fix 17/09/2026 : iframe native (le backend accepte ?token=), plus robuste que pdf.js/worker
        return (
          <div className="p-6">
            <iframe
              src={moduleSource + (moduleSource.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(localStorage.getItem('token') || '')}
              className="w-full h-[75vh] border rounded-lg shadow-sm"
              title={module.title}
            />
          </div>
        );

      case 'document': {
        const isOfficeDocument = /\.(docx?|xlsx?|pptx?)$/i.test(moduleSource);

        if (isOfficeDocument) {
          // Utiliser Google Docs Viewer pour un aperçu intégré
          const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(moduleSource)}&embedded=true`;
          return (
            <div className="w-full h-[80vh] p-2">
              <iframe
                src={viewerUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                title={module.title}
                className="border rounded-lg shadow-inner"
              ></iframe>
            </div>
          );
        } else {
          // Comportement par défaut pour les autres types de documents (téléchargement)
          return (
            <div className="flex flex-col items-center justify-center p-6 min-h-[300px]">
              <div className="text-center">
                <File className="h-16 w-16 mx-auto text-gray-400" />
                <h3 className="mt-4 text-lg font-medium">{module.title}</h3>
                <p className="mt-2 text-gray-500">
                  Document {getFileExtension(moduleSource).toUpperCase()}
                </p>
                <a 
                  href={moduleSource} 
                  download
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#FFB639] hover:bg-[#e5a333] transition-colors"
                  rel="noopener noreferrer"
                >
                  Télécharger le document
                </a>
              </div>
            </div>
          );
        }
      }

      default:
        return (
          <div className="p-6 text-center text-gray-500">
            <p>Type de module non pris en charge : {module.type}</p>
            {module.title && <p>Titre du module : {module.title}</p>}
            {module.fileType && <p>Type de fichier : {module.fileType}</p>}
          </div>
        );
    }
  };

  const getModuleIcon = () => {
    if (!module) return null;

    switch (module.type) {
      case 'video': return <Video className="h-5 w-5 mr-2" />;
      case 'text': return <FileText className="h-5 w-5 mr-2" />;
      case 'image': return <ImageIcon className="h-5 w-5 mr-2" />;
      case 'pdf': return <FileText className="h-5 w-5 mr-2" />;
      case 'document': return <File className="h-5 w-5 mr-2" />;
      default: return null;
    }
  };

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-8 text-gray-500">
          <p>Cours non trouvé (ID: {id})</p>
          <Link to="/admin/courses" className="text-[#FFB639] hover:underline">
            Retour aux cours
          </Link>
        </div>
      </Layout>
    );
  }

  if (!module) {
    return (
      <Layout>
        <div className="text-center py-8 text-gray-500">
          <p>Module non trouvé (ID: {moduleId}) dans le cours {course.title}</p>
          <Link to={`/admin/courses/${id}`} className="text-[#FFB639] hover:underline">
            Retour au cours
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <Link 
            to={`/admin/courses/${id}`} 
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour au cours
          </Link>
          <div className="flex items-center">
            {getModuleIcon()}
            <h1 className="text-2xl font-bold text-gray-900">
              {module.title} 
              <span className="text-base font-normal text-gray-500 ml-2">({module.type})</span>
            </h1>
          </div>
          <div className="text-gray-500">{module.duration} min</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {renderModuleContent()}
        </div>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
            <h3 className="text-lg font-semibold mb-2">Détails du Module</h3>
            <ul className="space-y-1 text-sm text-gray-700">
                <li><strong>ID:</strong> {module._id}</li>
                <li><strong>Titre:</strong> {module.title}</li>
                <li><strong>Type:</strong> {module.type}</li>
                <li><strong>Durée:</strong> {module.duration} min</li>
                <li className="break-all"><strong>Contenu:</strong> {moduleSource}</li>
                {module.fileType && <li><strong>Type de fichier:</strong> {module.fileType}</li>}
            </ul>
        </div>
      </div>
    </Layout>
  );
};

export default ModulePlayer;